import { useEffect, useState } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { useAuth, AuthRoute } from 'auth'
import { PageLoader, Page404 } from 'frame/components'
import { useSettings } from 'features/settings'
import { Security } from 'features/account/components'
import { Layout } from 'frame'
import { connect } from 'react-redux'
import { getEnabledFeatureToggleListRequest } from 'utils'

import {
  AUTH_LOGOUT_PATH,
  AUTH_VERIFICATION_PATH,
  AUTH_DRIVER_VERIFICATION_PATH,
  LogoutRoute,
  VerificationRoute,
  VerificationDriverRoute
} from 'features/auth'

import { ADMIN_PATH, AdminRoute } from 'features/admin'
import { HOMEPAGE_PATH, HomePageRoutes } from 'features/homepage'
import { DASHBOARD_PATH, DashboardRoutes } from 'features/dashboard'

import { constants as fromAccount, ModuleRoutes as AccountRoute } from 'features/account'
import { constants as fromDrivers, ModuleRoutes as DriverRoute } from 'features/drivers'
import { constants as fromFleet, ModuleRoutes as FleetRoute } from 'features/fleet'

import { REPORTS_PATH, ReportsRoute } from 'features/report'
import { FBT_PATH, FBTRoutes } from 'features/fbt'
import { FBT_WEB_FORM_PATH, ModuleRoutes as FbtWebFormRoutes } from 'features/fbt-web-form'

import { constants as fromRequestReliefVehicles, ModuleRoutes as ReliefVehicleRoute } from 'features/request-relief-vehicles'
import { constants as fromRequestVehicleRelocations, ModuleRoutes as RequestVehicleRelocationRoute } from 'features/request-vehicle-relocations'
import { constants as fromRequestRucSummary, ModuleRoutes as RequestRucSummaryRoute } from 'features/request-ruc-summary'
import { constants as fromRequestContractVariations, ModuleRoutes as RequestContractVariationRoute } from 'features/request-contract-variations'
import { constants as fromRequestVehicleSummary, ModuleRoutes as RequestVehicleSummary } from 'features/request-vehicle-summary'

import { MARKETING_PAGE_PATH, REGISTER_INTEREST_PAGE_PATH, MarketingPage } from 'features/marketing-page'

const ChangePassword = () => (
  <Layout>
    <Security />
  </Layout>
)

const AppRoutes = ({ fleetsReceived, initialized }) => {
  const navigate = useNavigate()

  const forceLogoutTrigger = localStorage.getItem('FORCE_LOGOUT_ALL_SESSIONS')

  const [fetchingFeatureToggles, setFetchingFeatureToggles] = useState(false)
  const [showFbtWebForm, setFbtWebForm] = useState(false)

  useEffect(() => {
    if (forceLogoutTrigger) {
      const isLocal = window.location.href.includes('local')
      !isLocal && navigate('/logout')
    }
  }, [forceLogoutTrigger, navigate])

  useEffect(() => {
    const fetchFeatureToggles = async () => {
      setFetchingFeatureToggles(true)
      const features = await getEnabledFeatureToggleListRequest()
      const FbtWebFormFeatureEnabled = features?.filter(f => f.feature === 'FbtWebForm')
      setFbtWebForm(FbtWebFormFeatureEnabled?.length > 0)
      setFetchingFeatureToggles(false)
    }
    fetchFeatureToggles()
  }, [])

  const { loading: authLoading } = useAuth()
  const { loading: accountLoading } = useSettings()

  if (authLoading || accountLoading || fetchingFeatureToggles) {
    return <PageLoader />
  }

  if (!fleetsReceived && initialized) {
    return (
      <Layout>
        <Routes>
          <Route path={AUTH_LOGOUT_PATH} element={<LogoutRoute />} />
          <Route path={AUTH_VERIFICATION_PATH} element={<VerificationRoute />} />
          <Route path={AUTH_DRIVER_VERIFICATION_PATH} element={<VerificationDriverRoute />} />
        </Routes>
      </Layout>
    )
  }

  return (
    <Routes>
      {/* Authentication */}
      <Route path={AUTH_LOGOUT_PATH} element={<LogoutRoute />} />
      <Route path={AUTH_VERIFICATION_PATH} element={<VerificationRoute />} />
      <Route path={AUTH_DRIVER_VERIFICATION_PATH} element={<VerificationDriverRoute />} />

      {/* Public */}
      <Route path={MARKETING_PAGE_PATH} element={<MarketingPage />} />
      <Route path={REGISTER_INTEREST_PAGE_PATH} element={<MarketingPage showForm />} />

      {showFbtWebForm ? (
        <Route path={`${FBT_WEB_FORM_PATH}/*`} element={<FbtWebFormRoutes />} />
      ) : (
        <Route path={`${FBT_WEB_FORM_PATH}/*`} element={<Page404 publicPage />} />
      )}

      {/* Protected */}
      <Route
        path={`${fromAccount.PATH_ALL}/*`}
        element={<AuthRoute><AccountRoute /></AuthRoute>}/>
      <Route
        path="/changepassword"
        element={<AuthRoute><ChangePassword /></AuthRoute>}/>
      <Route
        path={`${fromFleet.PATH_ALL}/*`}
        element={<AuthRoute><FleetRoute /></AuthRoute>}/>
      <Route
        path={`${fromDrivers.PATH_ALL}/*`}
        element={<AuthRoute><DriverRoute /></AuthRoute>}/>
      <Route
        path={`${fromRequestReliefVehicles.PATH_ALL}/*`}
        element={<AuthRoute><ReliefVehicleRoute /></AuthRoute>}/>
      <Route
        path={`${fromRequestVehicleRelocations.PATH_ALL}/*`}
        element={<AuthRoute><RequestVehicleRelocationRoute /></AuthRoute>}/>
      <Route
        path={`${fromRequestRucSummary.PATH_ALL}/*`}
        element={<AuthRoute><RequestRucSummaryRoute /></AuthRoute>}/>
      <Route
        path={`${fromRequestContractVariations.PATH_ALL}/*`}
        element={<AuthRoute><RequestContractVariationRoute /></AuthRoute>}/>
      <Route
        path={`${fromRequestVehicleSummary.PATH_ALL}/*`}
        element={<AuthRoute><RequestVehicleSummary /></AuthRoute>}/>
      <Route
        path={`${REPORTS_PATH}/*`}
        element={<AuthRoute><ReportsRoute /></AuthRoute>}/>

      <Route
        path={`${FBT_PATH}/*`}
        element={<AuthRoute><FBTRoutes /></AuthRoute>}/>
      <Route
        path={`${HOMEPAGE_PATH}/*`}
        element={<AuthRoute><HomePageRoutes /></AuthRoute>}/>
      <Route
        path={`${DASHBOARD_PATH}/*`}
        element={<AuthRoute><DashboardRoutes /></AuthRoute>}/>
      <Route
        path={`${ADMIN_PATH}/*`}
        element={<AuthRoute><AdminRoute /></AuthRoute>}/>

      {/* Default */}
      <Route
        path="/"
        element={<AuthRoute><HomePageRoutes /></AuthRoute>}/>
      <Route path="*" element={<Page404 />} />
    </Routes>
  )
}

const mapStateToProps = ({ settings }) => ({
  fleetsReceived: settings.fleetsReceived,
  initialized: settings.initialized,
})

export default connect(mapStateToProps)(AppRoutes)
